import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useLanguageStore } from '../../stores/languageStore';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useToastStore } from '../../stores/toastStore';
import { formatCurrency, formatDeactivatedAt } from '../../utils/formatters';
import { ReactivateRelanceModal } from './ReactivateRelanceModal';
import { CustomerFollowUpTimeline } from './CustomerFollowUpTimeline';
import { TaskItem } from '../../components/Dialogue/TaskItem';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import type { RelanceListItemDto } from '../../types/customerFollowUp/relance';

interface PaidRelanceItemProps {
    item: RelanceListItemDto;
}

// Row for the Paid list. Paid is not terminal: the row can be reactivated. That is only durable because
// RelanceRepository.ReactivateAsync also clears the Paid columns — without that the sync upsert
// re-freezes the row and it returns here on its own.
export const PaidRelanceItem: React.FC<PaidRelanceItemProps> = ({ item }) => {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);
    const reactivateTask = useRelanceDeactivationStore(s => s.reactivateTask);
    const showToast = useToastStore(s => s.showToast);
    const [modalOpen, setModalOpen] = useState(false);

    const paidDate = item.paidAt ? formatDeactivatedAt(item.paidAt, lang) : '';
    const badgeLabel = item.isFullPayment
        ? t(TranslationKey.Home.Paid.Row.BadgeFull)
        : t(TranslationKey.Home.Paid.Row.BadgePartial);

    const showRemainingBalance = item.isFullPayment === false && (item.paidRemainingBalance ?? 0) > 0;
    const remainingBalanceText = showRemainingBalance
        ? t(TranslationKey.Home.Paid.Row.RemainingBalance, {
              amount: formatCurrency(item.paidRemainingBalance ?? null, lang),
          })
        : null;

    const handleReactivate = async (reason: string | null) => {
        if (await reactivateTask(item.id, reason)) setModalOpen(false);
        else showToast('error', t(TranslationKey.ApiErrors.Generic));
    };

    return (
        <TaskItem
            item={{
                id: item.id,
                customerId: item.customerId,
                title: item.customerName,
                description: t(TranslationKey.Home.Paid.Row.PaidOn, { date: paidDate }),
                amount: formatCurrency(item.paidAmountReceived ?? null, lang),
                isCompleted: false,
            }}
            // The row is not a link: every action it offers sits in its own action bar.
            routePrefix="/not-found"
            testId={`paid-row-${item.id}`}
            note={item.paidNote ?? undefined}
            badge={
                <span className="flex items-center gap-s min-w-0">
                    <span
                        data-testid={`paid-row-badge-${item.id}`}
                        className={`text-xs font-medium w-fit px-1.5 py-0.5 rounded shrink-0 ${
                            item.isFullPayment
                                ? 'bg-surface-success text-success'
                                : 'bg-surface-warning text-warning'
                        }`}
                    >
                        {badgeLabel}
                    </span>
                    {remainingBalanceText && (
                        <span
                            data-testid={`paid-row-remaining-${item.id}`}
                            className="text-supporting text-content-subtle truncate"
                        >
                            {remainingBalanceText}
                        </span>
                    )}
                </span>
            }
            renderExpandedContent={() => <CustomerFollowUpTimeline customerId={item.customerId} relanceCount={item.relanceCount} statementSentOn={item.statementSentOn} />}
            renderExpandedActions={() => (
                <>
                    <div className="flex items-center justify-between gap-s px-l pb-xl">
                        <div className="flex items-center gap-m min-h-9">
                            <LinkButton
                                size="s"
                                onClick={() => setModalOpen(true)}
                                data-testid={`paid-row-reactivate-${item.id}`}
                            >
                                {t(TranslationKey.Home.Paid.Row.Reactivate)}
                            </LinkButton>
                        </div>
                    </div>
                    {modalOpen && (
                        <ReactivateRelanceModal
                            onConfirm={handleReactivate}
                            onCancel={() => setModalOpen(false)}
                        />
                    )}
                </>
            )}
        />
    );
};
